import { Button as AriaButton } from 'ariakit/button';
import type { ButtonHTMLAttributes, ForwardedRef } from 'react';
import { forwardRef, useMemo } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { useTheme } from 'styled-components';
import { variant } from 'styled-system';
import type { ITheme, TypographyVariant } from '../../utils/theme';
import Icon, { IconName } from '../icon/icon';
import { IconScale } from '../icon/types';
import Spinner from '../spinner/spinner';

export enum ButtonVariant {
  primary = 'primary',
  rounded = 'rounded',
  secondary = 'secondary',
  success = 'success',
  error = 'error',
  text = 'text',
}

export interface IButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  label: string;
  minWidth?: string;
  width?: string;
  isLoading?: boolean;
  typographyVariant?: TypographyVariant;
  iconName?: IconName;
  iconScale?: IconScale;
  isRightIcon?: boolean;
  selectedItemsCount?: number;
}

/**
 * @deprecated
 * Instead use: Button
 */
export const Button = forwardRef(
  (
    {
      variant = ButtonVariant.primary,
      label = '',
      minWidth,
      width,
      isLoading = false,
      typographyVariant,
      iconName,
      iconScale = IconScale.S,
      isRightIcon = false,
      selectedItemsCount,
      ...rest
    }: IButtonProps,
    ref: ForwardedRef<unknown>
  ) => {
    const theme = useTheme();

    const iconColor = useMemo(() => {
      const iconColorMap: Partial<Record<ButtonVariant, string>> = {
        [ButtonVariant.secondary]: theme.palette.neutral[100],
        [ButtonVariant.text]: 'currentColor', // Use currentColor to match button text color
      };

      return iconColorMap[variant] || theme.palette.neutral['00'];
    }, [variant, theme]);

    return (
      <StyledButton
        ref={ref}
        variant={variant}
        data-testid={label?.replaceAll(' ', '_')}
        {...rest}
        $typographyVariant={typographyVariant}
        width={width}
        $minWidth={minWidth}
        disabled={isLoading || rest.disabled}
        aria-label={
          iconName === IconName.ExternalLink
            ? `${label} opens in new tab`
            : `${rest['aria-label'] || label}`
        }
        $selectedItemsCount={selectedItemsCount}
      >
        {selectedItemsCount !== undefined && selectedItemsCount !== 0 && (
          <FloatingNumber className="floating-number">
            {selectedItemsCount}
          </FloatingNumber>
        )}
        {!isLoading && iconName && !isRightIcon && (
          <Icon
            name={iconName}
            color={iconColor}
            scale={iconScale}
            className="button-icon"
            ariaHidden
          />
        )}
        {isLoading ? <Spinner color={iconColor} /> : label}
        {!isLoading && iconName && isRightIcon && (
          <Icon
            name={iconName}
            color={iconColor}
            scale={iconScale}
            className="button-icon"
            ariaHidden
          />
        )}
      </StyledButton>
    );
  }
);

Button.displayName = 'DeprecatedButton';

export default Button;

const FloatingNumber = styled.div`
  ${({ theme }) => ({
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    background: theme.palette.black(1),
    color: theme.palette.neutral['00'],
    borderRadius: '50%',
    width: theme.spacing(2),
    height: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.labelSm.fontSize,
  })}
`;

const StyledButton: StyledComponent<
  typeof AriaButton,
  DefaultTheme,
  Record<string, unknown>
> = styled(AriaButton)(
  ({
    width,
    $minWidth,
    theme,
    $typographyVariant,
    $selectedItemsCount,
  }: {
    width: string;
    $minWidth?: string;
    theme: ITheme;
    $typographyVariant?: TypographyVariant;
    $selectedItemsCount?: number;
  }) => {
    const typography = $typographyVariant
      ? theme.typography[$typographyVariant]
      : theme.typography.bodyMd2;
    return {
      ...typography,
      border: $selectedItemsCount
        ? `1px solid ${theme.palette.black(1)}`
        : 'none',
      position: $selectedItemsCount ? 'relative' : 'static',
      width: width || '100%',
      minWidth: $minWidth || width || '240px',
      maxWidth: width || 'fit-content',
      minHeight: '38px',
      borderRadius: theme.borderRadius.sm,
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing('0px', 3),
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',

      '@media (max-width: 600px)': {
        width: '100%',
        minWidth: 'unset',
        maxWidth: 'unset',
      },

      '.button-icon': {
        marginTop: theme.spacing(0.5),
      },
    };
  },
  ({ theme }) => {
    const hoverFocusBorderColor = `${theme.palette.primary.light}33`;
    return variant({
      variants: {
        [ButtonVariant.primary]: {
          bg: theme.palette.primary.main,
          color: theme.palette.neutral['00'],
          ':hover': {
            bg: theme.palette.primary.dark,
          },
          ':focus-visible': {
            bg: theme.palette.primary.dark,
            outline: `2px solid ${theme.palette.focus}`,
            outlineOffset: '1px',
          },
          ':disabled': {
            bg: theme.palette.primary.main,
            opacity: 0.4,
          },
        },
        [ButtonVariant.secondary]: {
          bg: theme.palette.neutral['00'],
          color: theme.palette.neutral[100],
          boxShadow: `0 0 0 0.5px ${theme.palette.neutral[80]}`,
          ':hover': {
            bg: theme.palette.primary.bg,
            color: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${hoverFocusBorderColor}`,
          },
          ':focus-visible': {
            bg: theme.palette.primary.bg,
            color: theme.palette.primary.main,
            boxShadow: `0 0 0 1px ${hoverFocusBorderColor}`,
            outline: `2px solid ${theme.palette.focus}`,
            outlineOffset: '1px',
          },
          ':disabled': {
            bg: theme.palette.neutral['00'],
            color: theme.palette.neutral[60],
            boxShadow: `0 0 0 1px ${theme.palette.neutral[60]}`,
          },
        },
        [ButtonVariant.rounded]: {
          bg: theme.palette.primary.bg,
          color: theme.palette.primary.main,
          boxShadow: `0 0 0 1px ${theme.palette.primary.light}`,
          borderRadius: '38px',
          minWidth: 'auto',
          padding: theme.spacing(1, 1.5),
          ':hover': {
            bg: theme.palette.neutral['00'],
            boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
          },
          ':focus-visible': {
            outline: `2px solid ${theme.palette.focus}`,
          },
          ':disabled': {
            opacity: 0.4,
          },
        },
        [ButtonVariant.success]: {
          bg: theme.palette.success.main,
          color: theme.palette.neutral['00'],
          ':hover': {
            bg: theme.palette.success.dark,
          },
          ':focus-visible': {
            bg: theme.palette.success.dark,
            outline: `2px solid ${theme.palette.focus}`,
            outlineOffset: '1px',
          },
          ':disabled': {
            bg: theme.palette.success.main,
            opacity: 0.4,
          },
        },
        [ButtonVariant.error]: {
          bg: theme.palette.error.main,
          color: theme.palette.neutral['00'],
          ':hover': {
            bg: theme.palette.error.dark,
          },
          ':focus-visible': {
            bg: theme.palette.error.dark,
            outline: `2px solid ${theme.palette.focus}`,
            outlineOffset: '1px',
          },
          ':disabled': {
            bg: theme.palette.error.main,
            opacity: 0.4,
          },
        },
        [ButtonVariant.text]: {
          bg: 'transparent',
          minWidth: 'auto',
          minHeight: 'auto',
          padding: theme.spacing(0),
          color: theme.palette.primary.main,
          borderRadius: '0px',
          ':hover': {
            boxShadow: `0 2px 0px 0px ${theme.palette.primary.main}`,
          },
          ':focus-visible': {
            boxShadow: `0px 2px 0px 0px ${theme.palette.primary.main}`,
          },
          ':disabled': {
            opacity: 0.4,
          },
        },
      },
    });
  }
);
